import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Pause, Download, CreditCard, Trash2, X } from "lucide-react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

/**
 * PausedScreen — Phase 64.G + 64.H.
 *
 * Shown to members whose trial expired AND who didn't pick a plan within the
 * 7-day grace window. Their membership_status is "paused". They have:
 *  - Read-only access to their own data (view + export)
 *  - Pause stays INDEFINITELY — no auto-deletion (members on parental /
 *    medical leave should never lose their account)
 *  - Option to permanently delete account ONLY if they explicitly trigger it
 *    (DELETE /api/account/me with password + typed confirmation)
 */
const PausedScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    const r = await fetch(`${API}/account/export`, { credentials: "include" });
    if (!r.ok) {
      toast.error("Export failed. Please try again.");
      return;
    }
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dhdh-export-${user?.user_id || "me"}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  };

  const triggerDelete = async () => {
    if (confirmText.trim() !== "DELETE MY ACCOUNT" || !pwd) {
      toast.error('Type DELETE MY ACCOUNT and enter your password to continue');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/account/me`, { data: { password: pwd, confirmation: confirmText } });
      toast.success("Account deleted. Goodbye!");
      window.location.href = "/";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deletion failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 sm:p-8 lg:p-12" data-testid="paused-screen">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#FCD34D] rounded-2xl p-6 sm:p-8 mb-6 shadow-sm"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#FEF3C7] flex items-center justify-center shrink-0">
              <Pause className="w-6 h-6 text-[#D97706]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#D97706] mb-1">Account paused</p>
              <h1
                className="text-2xl sm:text-3xl font-medium text-[#92400E]"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="paused-heading"
              >
                Your DHDH membership is on hold
              </h1>
            </div>
          </div>
          <p className="text-[#475569] leading-relaxed">
            Your trial ended and a plan wasn't chosen, so your account is paused. <strong>Your account stays paused indefinitely</strong> — perfect if you're on parental leave, medical leave, or just taking a break. We'll never auto-delete it.
          </p>
          <p className="text-[#475569] leading-relaxed mt-2">
            You can still view and export your own data, but features are read-only until you reactivate.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Reactivate CTA */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => navigate("/membership")}
            className="text-left bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] text-white rounded-2xl p-6 hover:shadow-lg transition-shadow"
            data-testid="reactivate-cta"
          >
            <CreditCard className="w-6 h-6 mb-3" />
            <p className="font-medium text-lg mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Reactivate my membership
            </p>
            <p className="text-sm text-white/85 leading-relaxed">
              Pick a plan to restore full access. Beta and founding members keep their locked rate.
            </p>
          </motion.button>

          {/* Export CTA */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            onClick={exportData}
            className="text-left bg-white border border-[#E5DFEC] rounded-2xl p-6 hover:bg-[#FAF7F2] transition-colors"
            data-testid="export-data-cta"
          >
            <Download className="w-6 h-6 mb-3 text-[#6B4E8A]" />
            <p className="font-medium text-lg mb-1 text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Export all my data
            </p>
            <p className="text-sm text-[#64748B] leading-relaxed">
              Download a JSON file with everything you own — CE records, notes, mileage, shifts, and more.
            </p>
          </motion.button>
        </div>

        {/* Delete account — opt-in only */}
        <div className="mt-8 pt-6 border-t border-[#E5DFEC]">
          <button
            onClick={() => setDeleteOpen(true)}
            className="text-xs text-[#94A3B8] hover:text-[#DC2626] underline transition-colors"
            data-testid="open-delete-modal"
          >
            Permanently delete my account
          </button>
          <p className="text-[10px] text-[#94A3B8] mt-1 max-w-md">
            We'll never auto-delete — only you can trigger this. Once deleted, all your data is gone forever.
          </p>
        </div>

        {/* Delete confirmation modal */}
        {deleteOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => !deleting && setDeleteOpen(false)}
            data-testid="delete-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 sm:p-7 max-w-md w-full"
              data-testid="delete-modal"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] flex items-center justify-center mb-3">
                    <Trash2 className="w-5 h-5 text-[#DC2626]" />
                  </div>
                  <h2 className="text-xl font-medium text-[#DC2626]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Delete your account?
                  </h2>
                </div>
                <button
                  onClick={() => !deleting && setDeleteOpen(false)}
                  className="text-[#94A3B8] hover:text-[#475569]"
                  data-testid="close-delete-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-[#475569] leading-relaxed mb-4">
                This permanently deletes your account and everything in it: discussions, comments, notes, CE records, mileage, shifts, receipts, health logs, and more. <strong className="text-[#DC2626]">This cannot be undone.</strong>
              </p>
              <p className="text-xs text-[#64748B] mb-1">Re-enter your password</p>
              <Input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Your password"
                className="mb-3"
                disabled={deleting}
                data-testid="delete-password-input"
              />
              <p className="text-xs text-[#64748B] mb-1">
                Type <strong className="text-[#DC2626]">DELETE MY ACCOUNT</strong> to confirm
              </p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className="mb-4"
                disabled={deleting}
                data-testid="delete-confirm-input"
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteOpen(false)}
                  disabled={deleting}
                  className="rounded-full"
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={triggerDelete}
                  disabled={deleting || confirmText.trim() !== "DELETE MY ACCOUNT" || !pwd}
                  className="rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? "Deleting…" : "Delete forever"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        <p className="text-xs text-[#94A3B8] text-center mt-6">
          Questions? Email{" "}
          <a href="mailto:support@dhdh.ca" className="text-[#6B4E8A] hover:underline">
            support@dhdh.ca
          </a>
        </p>
      </div>
    </Layout>
  );
};

export default PausedScreen;
